import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserBusiness } from "@/features/settings/hooks/useUserBusiness";
import { useActivityImageUpload } from "./useActivityImageUpload";
import {
  partnerActivitiesRepo,
  type ActivityDraft,
  type ActivityPatch,
} from "@/data/repos/partnerActivitiesRepo";
import type { Activity } from "@/domain/activity/types";

export function useActivities() {
  const { businessId } = useUserBusiness();
  const queryClient = useQueryClient();
  const { deleteImage } = useActivityImageUpload();

  const activitiesQuery = useQuery<Activity[]>({
    queryKey: ["activities", businessId],
    queryFn: () => partnerActivitiesRepo.list(),
    enabled: !!businessId,
  });

  const subcategoriesQuery = useQuery({
    queryKey: ["activity-taxonomy"],
    queryFn: () => partnerActivitiesRepo.listSubcategories(),
  });

  // The activity carries the subcategory's id, not the subcategory — the
  // taxonomy the picker already loads is where its Czech label comes from.
  const activities = useMemo(() => {
    const byId = new Map((subcategoriesQuery.data ?? []).map((sub) => [sub.id, sub]));
    return (activitiesQuery.data ?? []).map((activity) => ({
      ...activity,
      subcategory: activity.subcategoryId === null ? null : byId.get(activity.subcategoryId) ?? null,
    }));
  }, [activitiesQuery.data, subcategoriesQuery.data]);

  const createActivity = useMutation({
    mutationFn: (draft: ActivityDraft) => partnerActivitiesRepo.create(draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", businessId] });
    },
  });

  const updateActivity = useMutation({
    mutationFn: ({ id, ...patch }: ActivityPatch & { id: number }) =>
      partnerActivitiesRepo.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", businessId] });
      queryClient.invalidateQueries({ queryKey: ["offer-activity-options"] });
    },
  });

  const deleteActivity = useMutation({
    mutationFn: async (activityId: number) => {
      // The photo has to be read before the row goes: the API's delete answers
      // with an id only, and afterwards nothing knows which file was the
      // activity's, so it would sit in the bucket forever.
      const imageUrl = activities.find((activity) => activity.id === activityId)?.imageUrl ?? null;
      await partnerActivitiesRepo.remove(activityId);
      if (imageUrl) {
        deleteImage(imageUrl).catch((err) =>
          console.error("activity image cleanup failed", err),
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities", businessId] });
    },
  });

  return {
    activities,
    subcategories: subcategoriesQuery.data ?? [],
    isLoading: activitiesQuery.isLoading || subcategoriesQuery.isLoading,
    error: activitiesQuery.error ?? subcategoriesQuery.error,
    createActivity,
    updateActivity,
    deleteActivity,
  };
}
